import { useCallback, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import type { Theme } from "@mui/material";
import { styled, Typography, useMediaQuery } from "@mui/material";

import {
  Body as BaseBody,
  ContentContainer,
  Header,
  NavigateBeforeButton,
  FullScreenLoader,
} from "@components";
import { RequestType } from "@interfaces";
import {
  useSetRequestDocumentType,
  useRequestData,
  useClearRequest,
  useDoctorDocumentReadiness,
} from "@hooks";
import { DoctorDocumentReadinessModal } from "@components/documentCreation";
import { Card } from "../../NewDocumentRequest/components/Card";
import { UpdateRequestUnknownErrorModal } from "./UpdateRequestUnknownErrorModal";

const Title = styled(Typography)(({ theme }) => ({
  [theme.breakpoints.down("md")]: {
    display: "none",
  },
}));

const Body = styled(BaseBody)(() => ({
  alignItems: "normal",
}));

function redirectToResolver(requestType: RequestType, requestId: string) {
  switch (requestType) {
    case RequestType.PRESCRIPTION:
      return `/app/solicitudes/${requestId}/receta/crear/buscar`;
    case RequestType.COMPOUNDED_PRESCRIPTION:
      return `/app/solicitudes/${requestId}/receta-magistral/crear`;
    case RequestType.MEDICAL_ORDER:
      return `/app/solicitudes/${requestId}/orden-medica/crear/buscar`;
    case RequestType.MEDICAL_CERTIFICATE:
      return `/app/solicitudes/${requestId}/certificado/crear`;
    default:
      return `/app/solicitudes/${requestId}/gestionar/seleccionar-documento`;
  }
}

export function SelectDocument() {
  const navigate = useNavigate();
  const isDesktop = useMediaQuery((theme: Theme) => theme.breakpoints.up("md"));
  const { requestId = "" } = useParams();
  const { data: requestData } = useRequestData();
  const { mutate: setRequestDocumentType } = useSetRequestDocumentType();
  const { mutate: clearRequest } = useClearRequest();

  const [isUpdateRequestErrorModalOpen, setIsUpdateRequestErrorModalOpen] =
    useState(false);

  const [isLoading, setIsLoading] = useState(false);

  const { checkSigningBlock, modalProps } = useDoctorDocumentReadiness({
    requestId,
  });

  const handleDocumentTypeClick = useCallback(
    async (documentType: RequestType) => {
      if (isLoading) return;

      setIsLoading(true);

      try {
        // Past the deadline, a doctor creates no document of any type until
        // they validate their identity, so the request is left as it was.
        if (await checkSigningBlock()) {
          setIsLoading(false);

          return;
        }

        if (requestData && requestData.request.documentType !== documentType) {
          // Update the request document type when it's different from the current one
          await setRequestDocumentType({ requestId, documentType });
          if (requestData.request.documentType in RequestType) {
            // Clear existing request data since the document type changed
            // This prevents conflicts with previously saved items in the database
            await clearRequest({ requestId });
          }
        }

        navigate(redirectToResolver(documentType, requestId));
      } catch {
        setIsUpdateRequestErrorModalOpen(true);
        setIsLoading(false);
      }
    },
    [
      navigate,
      requestId,
      setRequestDocumentType,
      clearRequest,
      requestData,
      isLoading,
      checkSigningBlock,
    ],
  );

  if (isLoading) {
    return (
      <>
        <Header
          useMekiDocIcon={isDesktop}
          title={!isDesktop ? "Tipo de documento" : undefined}
          titleColor="black"
          left={
            <NavigateBeforeButton
              onClick={() => navigate(`/app/solicitudes/${requestId}/gestionar`)}
            />
          }
        />
        <ContentContainer flex={1} disableGutters>
          <Body justifyContent="center">
            <FullScreenLoader title="Cargando..." />
          </Body>
        </ContentContainer>
      </>
    );
  }

  return (
    <>
      <Header
        useMekiDocIcon={isDesktop}
        title={!isDesktop ? "Tipo de documento" : undefined}
        titleColor="black"
        left={
          <NavigateBeforeButton
            onClick={() => navigate(`/app/solicitudes/${requestId}/gestionar`)}
          />
        }
      />
      <ContentContainer disableGutters>
        <Body>
          <Title variant="h4" fontWeight={"medium"}>
            Tipo de documento
          </Title>
          {Object.values(RequestType).map((documentType) => (
            <Card
              key={documentType}
              documentType={documentType}
              onClick={() => handleDocumentTypeClick(documentType)}
            />
          ))}
        </Body>
      </ContentContainer>
      <UpdateRequestUnknownErrorModal isOpen={isUpdateRequestErrorModalOpen} />
      <DoctorDocumentReadinessModal {...modalProps} />
    </>
  );
}
